import React, { useState, useEffect } from 'react';
import { Campaign, Technicentre, EmailData } from '../../types';

interface EmailModalProps {
  show: boolean;
  campaigns: Campaign[];
  technicentres: Technicentre[];
  onSend: (data: EmailData) => void;
  onClose: () => void;
}

// ===== Composant EmailModal =====
const EmailModal: React.FC<EmailModalProps> = ({
  show,
  campaigns,
  technicentres,
  onSend,
  onClose,
}) => {

  const [formData, setFormData] = useState<EmailData>({});
  const [selectionType, setSelectionType] = useState<'dates' | 'weeks'>('weeks');
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [selectedTechnicentreEmail, setSelectedTechnicentreEmail] = useState<string>('');

  useEffect(() => {
    if (show) {
      const currentWeek = getWeekNumber(new Date());
      const defaultWeeks: number[] = [];
      for (let offset = 0; offset < 5; offset++) {
        const nextWeek = ((currentWeek - 1 + offset) % 52) + 1;
        defaultWeeks.push(nextWeek);
      }
      setSelectedWeeks(defaultWeeks);
    }
  }, [show]);

  const getWeekNumber = (date: Date): number => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  const renderWeekCheckboxes = () => {
    const weeks = [];
    for (let week = 1; week <= 52; week++) {
      weeks.push(
        <label key={week} className="flex items-center p-1 cursor-pointer">
          <input
            type="checkbox"
            checked={selectedWeeks.includes(week)}
            onChange={() => handleWeekToggle(week)}
          />
          <span className="ml-1 text-sm">S{week}</span>
        </label>
      );
    }
    return weeks;
  };

  const handleTechnicentreChange = (technicentreName: string) => {
    setFormData(prev => ({ ...prev, technicentre: technicentreName }));

    const selectedTc = technicentres.find(tc => tc.nom === technicentreName);
    setSelectedTechnicentreEmail(selectedTc?.email || '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: EmailData = {
      campaign: formData.campaign,
      technicentre: formData.technicentre,
      include_validated: formData.include_validated,
      include_refused: formData.include_refused,
      technicentre_email: selectedTechnicentreEmail,
    };

    if (selectionType === 'weeks') {
      if (selectedWeeks.length === 0) {
        alert('Veuillez sélectionner au moins une semaine');
        return;
      }
      data.week_numbers = selectedWeeks;
    } else {
      if (!formData.date_debut || !formData.date_fin) {
        alert('Veuillez sélectionner les dates');
        return;
      }
      data.date_debut = formData.date_debut;
      data.date_fin = formData.date_fin;
    }

    onSend(data);
  };

  const handleWeekToggle = (week: number) => {
    setSelectedWeeks(prev =>
      prev.includes(week)
        ? prev.filter(w => w !== week)
        : [...prev, week]
    );
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        {}
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <i className="fas fa-paper-plane mr-3 text-green-600"></i> Envoi d'interventions
          </h3>
          <button onClick={onClose} className="modal-close text-gray-400 hover:text-gray-600 text-xl">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {}
        <form onSubmit={handleSubmit} className="space-y-6">
          {}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">
                <i className="fas fa-flag mr-1"></i> Campagne
              </label>
              <select
                value={formData.campaign || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, campaign: e.target.value }))}
                className="form-input"
              >
                <option value="">Toutes les campagnes</option>
                {campaigns.map((campaign) => (
                  <option key={campaign.id} value={campaign.nom}>
                    {campaign.nom}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">
                <i className="fas fa-building mr-1"></i> Technicentre
              </label>
              <select
                value={formData.technicentre || ''}
                onChange={(e) => handleTechnicentreChange(e.target.value)}
                className="form-input"
              >
                <option value="">Tous les technicentres</option>
                {technicentres.map((technicentre) => (
                  <option key={technicentre.id} value={technicentre.nom}>
                    {technicentre.nom}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {}
          <div className="text-center">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-calendar-week mr-1"></i> Type de sélection
            </label>
            <div className="space-y-2 flex flex-col items-center">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="selectionType"
                  value="weeks"
                  checked={selectionType === 'weeks'}
                  onChange={(e) => setSelectionType(e.target.value as 'weeks')}
                  className="mr-2"
                />
                <span>Par semaines</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="selectionType"
                  value="dates"
                  checked={selectionType === 'dates'}
                  onChange={(e) => setSelectionType(e.target.value as 'dates')}
                  className="mr-2"
                />
                <span>Par dates</span>
              </label>
            </div>
          </div>

          {}
          {selectionType === 'weeks' ? (
            <div className="text-center">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-calendar-week mr-1"></i> Sélectionner les semaines
              </label>
              <div className="grid grid-cols-5 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg mx-auto" style={{maxWidth: 'fit-content'}}>
                {renderWeekCheckboxes()}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">
                  <i className="fas fa-calendar-alt mr-1"></i> Date début
                </label>
                <input
                  type="date"
                  value={formData.date_debut || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_debut: e.target.value }))}
                  className="form-input"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 text-center">
                  <i className="fas fa-calendar-alt mr-1"></i> Date fin
                </label>
                <input
                  type="date"
                  value={formData.date_fin || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, date_fin: e.target.value }))}
                  className="form-input"
                />
              </div>
            </div>
          )}

          {}
          <div className="space-y-3 border-t pt-4 text-center">
            <div className="flex flex-col items-center space-y-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.include_validated || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, include_validated: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm">Inclure les interventions validées</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.include_refused || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, include_refused: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm">Inclure les interventions refusées</span>
              </label>
            </div>
          </div>

          {}
          <div className="flex gap-4">
            <button type="submit" className="flex-1 btn btn-success">
              <i className="fas fa-paper-plane mr-2"></i> Envoyer
            </button>
            <button type="button" onClick={onClose} className="btn btn-cancel ml-auto">
              <i className="fas fa-times mr-2"></i> Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmailModal;
